import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { useUrlFilterState } from '../../lib/url-filter-state';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { ReportAnalyticsGrid, ReportBarChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { lastMonthDateFilters } from './report-filters';
import { fetchDischargeReport } from './reports.api';
import { useDischargeReport } from './reports.queries';
import type { DischargeReportFilters, DischargeReportRow } from './types';

const title = 'Discharge Report';
const initialFilters = (): DischargeReportFilters => ({ page: 1, limit: 25, ...lastMonthDateFilters(), value: '', dischargeStatus: '' });
const text = (value: string | null | undefined) => value || '-';
const upper = (value: string | null | undefined) => value?.trim() ? value.toUpperCase() : '-';
const doctorText = (value: string) => {
  const name = value.trim().replace(/^(dr\.?\s+)+/i, '');
  return name && name !== '-' ? `DR. ${name}` : '-';
};

export const DischargeReportPage = () => {
  const [filters, setFilters] = useUrlFilterState<DischargeReportFilters>(initialFilters);
  const [draft, setDraft] = useState<DischargeReportFilters>(filters);
  const query = useDischargeReport(filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...draft, sortBy: current.sortBy, sortOrder: current.sortOrder, page: 1 }));
  };
  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };
  const changeLimit = (limit: number) => {
    setDraft((current) => ({ ...current, limit }));
    setFilters((current) => ({ ...current, limit, page: 1 }));
  };
  const rows = query.data?.data ?? [];
  const summary = query.data?.summary;
  const genderChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'Male', value: summary?.gender.male ?? 0, color: '#3777b7' },
    { name: 'Female', value: summary?.gender.female ?? 0, color: '#b1424d' },
    { name: 'Others', value: summary?.gender.others ?? 0, color: '#c98a1c' },
  ], [summary?.gender.female, summary?.gender.male, summary?.gender.others]);
  const typeChartData = useMemo<ReportChartDatum[]>(() => (summary?.byType ?? []).map((item, index) => ({
    name: item.type,
    Count: item.count,
    color: reportChartColor(index),
  })), [summary?.byType]);
  const doctorChartData = useMemo<ReportChartDatum[]>(() => [...(summary?.byDoctor ?? [])]
    .sort((left, right) => right.count - left.count)
    .slice(0, 8)
    .map((item, index) => ({
      name: item.doctor,
      Patients: item.count,
      color: reportChartColor(index),
    })), [summary?.byDoctor]);
  const errorMessage = query.error instanceof ApiError ? query.error.message : `${title} could not be loaded.`;

  const exportToExcel = async () => {
    const exportRows = await fetchAllPages<DischargeReportRow>((page, limit) => fetchDischargeReport({ ...filters, page, limit }), 500);
    await exportRowsToExcel({
      fileName: `${title.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}`,
      title,
      subtitle: filters.fromDate && filters.toDate ? `${filters.fromDate} to ${filters.toDate}` : 'All dates',
      columns: [
        { header: 'Bill ID', width: 16, map: (row) => upper(row.billId) },
        { header: 'Patient', width: 30, map: (row) => upper(patientText(row)) },
        { header: 'Gender', width: 12, map: (row) => upper(row.gender) },
        { header: 'Doctor', width: 24, map: (row) => upper(doctorText(row.doctorName)) },
        { header: 'Admission Date', width: 20, map: (row) => row.admissionDate ? formatIpdDateTime(row.admissionDate) : '-' },
        { header: 'Discharge Date', width: 20, map: (row) => formatIpdDateTime(row.dischargeDate) },
        { header: 'Days', width: 10, align: 'right', map: (row) => row.days ?? 0 },
        { header: 'Discharge Type', width: 16, map: (row) => upper(row.dischargeType) },
      ],
      rows: exportRows,
    });
  };

  return (
    <div className="page-stack report-list-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>{title}</h1>
          <p>Finalized discharge records by patient, duration, and type.</p>
        </div>
      </div>

      <form className="report-list-filter-card discharge-report-filters" onSubmit={applyFilters}>
        <label>
          From date
          <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
        </label>
        <label>
          To date
          <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
        </label>
        <label>
          Type
          <select value={draft.dischargeStatus ?? ''} onChange={(event) => setDraft((current) => ({ ...current, dischargeStatus: event.target.value as DischargeReportFilters['dischargeStatus'] }))}>
            <option value="">All Types</option>
            <option value="Normal">Normal</option>
            <option value="Referral">Referral</option>
            <option value="LAMA">LAMA</option>
            <option value="DORB">DORB</option>
            <option value="DAMA">DAMA</option>
            <option value="Death">Death</option>
          </select>
        </label>
        <label>
          Search
          <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Patient, UHID, doctor, bill" />
        </label>
        <button className="button button-primary report-list-action" type="submit"><IconSearch />Search</button>
        <button className="button button-secondary report-list-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
        <ExcelExportButton className="report-list-action" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
      </form>

      <section className="report-summary-grid">
        <SummaryTile label="Records" value={summary?.count ?? 0} />
        <SummaryTile label="Male" value={summary?.gender.male ?? 0} />
        <SummaryTile label="Female" value={summary?.gender.female ?? 0} />
        <SummaryTile label="Others" value={summary?.gender.others ?? 0} />
      </section>

      {query.isSuccess ? (
        <ReportAnalyticsGrid columns={3}>
          <ReportDonutChartCard eyebrow="Gender analysis" title={`${title} gender distribution`} data={genderChartData} />
          <ReportBarChartCard
            eyebrow="Type analysis"
            title="Discharge type comparison"
            data={typeChartData}
            bars={[{ key: 'Count', name: 'Patients', color: '#0d6b66' }]}
          />
          <ReportBarChartCard
            eyebrow="Doctor analysis"
            title={`${title} by doctor`}
            data={doctorChartData}
            bars={[{ key: 'Patients', name: 'Patients', color: '#3777b7' }]}
            layout="vertical"
            height={Math.max(240, doctorChartData.length * 34 + 90)}
          />
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card report-list-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title={`Unable to load ${title}`} message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title="No discharge rows found" message="Change filters or date range and search again." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <>
            <div className="report-table-scroll">
              <table className="report-data-table discharge-report-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <SortableTh control={tableSort} column="billId">Bill ID</SortableTh>
                    <SortableTh control={tableSort} column="patient">Patient</SortableTh>
                    <th>Gender</th>
                    <SortableTh control={tableSort} column="doctor">Doctor</SortableTh>
                    <SortableTh control={tableSort} column="admissionDate">Admission Date</SortableTh>
                    <SortableTh control={tableSort} column="dischargeDate">Discharge Date</SortableTh>
                    <SortableTh control={tableSort} column="days">Days</SortableTh>
                    <SortableTh control={tableSort} column="dischargeType">Type</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                      <td>
                        {row.billId
                          ? <Link className="bill-id-link" to={`/ipd-daycare/${row.admissionId}?tab=bill-details`} title="Open bill details"><strong>{row.billId}</strong></Link>
                          : <strong>-</strong>}
                      </td>
                      <td>
                        {row.patientId
                          ? <Link className="patient-name-link" to={`/patients/${row.patientId}`} title="Open patient profile">{patientText(row)}</Link>
                          : patientText(row)}
                      </td>
                      <td>{text(row.gender)}</td>
                      <td>{doctorText(row.doctorName)}</td>
                      <td>{row.admissionDate ? formatIpdDateTime(row.admissionDate) : '-'}</td>
                      <td>{formatIpdDateTime(row.dischargeDate)}</td>
                      <td className="number">{row.days ?? '-'}</td>
                      <td>{row.dischargeType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <div className="report-summary-tile">
    <span>{label}</span>
    <strong>{value.toLocaleString('en-IN')}</strong>
  </div>
);

const patientText = (row: Pick<DischargeReportRow, 'patientName' | 'uhid'>) =>
  row.uhid ? `${row.patientName} (${row.uhid})` : row.patientName;
